import { Component, EventEmitter, inject, Input, Output } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { finalize } from 'rxjs';
import { WirelessSetupService } from '../../../common/services/wireless-setup.service';
import { NotificationService } from '../../../common/services/notification.service';
import { SpinnerService } from '../../../common/services/spinner.service';
import { LanguageStateService } from '../../../common/services/language-state.service';
import { CommonUtil } from '../../../common/util/common-util';

export interface ValidatedAccountDetails {
  mobileNumber: string;
  fanNumber: string;
  fanPin: string;
}

export interface AccountValidatedEvent {
  accountNumber: number;
  authUserList: string[];
  validatedAccount: ValidatedAccountDetails;
  pinDeliveryOptions: { email?: string; ctnOptions: string[] };
}

@Component({
    selector: 'app-account-lookup',
    templateUrl: './account-lookup.component.html',
    styleUrl: './account-lookup.component.scss',
    standalone: false
})
export class AccountLookupComponent {
  @Input() wirelessSetupCustomerOrderPayload: any;
  @Input() connectionId: string | undefined;
  @Output() accountValidated = new EventEmitter<AccountValidatedEvent>();
  @Output() clearSelectionRequested = new EventEmitter<void>();
  @Output() connectionError = new EventEmitter<void>();

  fb: FormBuilder = inject(FormBuilder);
  wirelessSetupService = inject(WirelessSetupService);
  notificationService = inject(NotificationService);
  spinnerService = inject(SpinnerService);
  languageService: LanguageStateService = inject(LanguageStateService);

  form: FormGroup = this.fb.group({
    mobileNumber: ['', [Validators.required, Validators.pattern(/^\d{3}-\d{3}-\d{4}$/)]],
    fanNumber: ['', [Validators.required]],
    fanPin: ['', [Validators.required]],
  });

  isAccountValidated = false;
  isFanPinMasked = true;
  validatedAccount: ValidatedAccountDetails | null = null;
  showFanNumberInfo = false;
  showFanPinInfo = false;
  accountNotValidatedError = false;

  private accountNumber: number;
  private authUserList: string[] = [];

  toggleFanPinMask() {
    this.isFanPinMasked = !this.isFanPinMasked;
  }

  onFanNumberInfoVisibilityChange(visible: boolean) {
    this.showFanNumberInfo = visible;
    if (visible) this.showFanPinInfo = false;
  }

  onFanPinInfoVisibilityChange(visible: boolean) {
    this.showFanPinInfo = visible;
    if (visible) this.showFanNumberInfo = false;
  }

  formatPhone(event: any, controlName: string) {
    const control = this.form.get(controlName);
    if (!control) return;

    const input = event.target.value.replace(/\D/g, '').substring(0, 10);

    if (!input) {
      control.setValue('', { emitEvent: false });
      return;
    }

    const areaCode = input.substring(0, 3);
    const middle = input.substring(3, 6);
    const last = input.substring(6);

    let formattedValue = '';
    if (input.length > 6) {
      formattedValue = `${areaCode}-${middle}-${last}`;
    } else if (input.length > 3) {
      formattedValue = `${areaCode}-${middle}`;
    } else {
      formattedValue = areaCode;
    }

    if (control.value !== formattedValue) {
      control.setValue(formattedValue, { emitEvent: false });
      setTimeout(() => {
        const inputElement = event.target as HTMLInputElement;
        inputElement.setSelectionRange(formattedValue.length, formattedValue.length);
      });
    }
  }

  onlyNumbers(event: KeyboardEvent): boolean {
    return CommonUtil.onlyNumbers(event);
  }

  clearField(controlName: string) {
    this.form.get(controlName)?.setValue('');
  }

  validateAccountClicked() {
    if (!this.form.valid) {
      this.form.markAllAsTouched();
      return;
    }

    this.spinnerService.setLoading(true, 'account-lookup-validateAccount');
    this.accountNotValidatedError = false;

    const formValues = this.form.value;
    const body = {
      TransactionNumber: this.sanitizePhoneNumber(formValues.mobileNumber),
      FanNumber: formValues.fanNumber,
      FanPin: formValues.fanPin,
    };

    this.wirelessSetupService.authCheckFan(body, this.wirelessSetupCustomerOrderPayload)
      .pipe(finalize(() => this.spinnerService.setLoading(false, 'account-lookup-validateAccount')))
      .subscribe({
        next: (resp: any) => {
          const viResp = this.retrieveWirelessResponse(resp);
          if (viResp.Code === '0000') {
            this.accountNumber = viResp.AccountNumber;
            this.authUserList = viResp.AuthUserList?.split(',').map((a: string) => a.trim()) || [];

            this.validatedAccount = {
              mobileNumber: formValues.mobileNumber,
              fanNumber: formValues.fanNumber,
              fanPin: formValues.fanPin,
            };
            this.isAccountValidated = true;
            this.form.disable();

            const ctnOptions = viResp.CtnList
              ? viResp.CtnList.split(',').map((ctn: string) => ctn.trim()).filter(Boolean)
              : [];

            this.accountValidated.emit({
              accountNumber: this.accountNumber,
              authUserList: this.authUserList,
              validatedAccount: this.validatedAccount,
              pinDeliveryOptions: { email: viResp.Email || undefined, ctnOptions }
            });
          } else {
            this.accountNotValidatedError = true;
          }
        },
        error: () => {
          this.connectionError.emit();
        }
      });
  }

  clearSelectionClicked() {
    this.clearSelectionRequested.emit();
  }

  reset() {
    this.form.reset();
    this.form.enable();
    this.isAccountValidated = false;
    this.validatedAccount = null;
    this.isFanPinMasked = true;
    this.accountNotValidatedError = false;
    this.showFanNumberInfo = false;
    this.showFanPinInfo = false;
  }

  private sanitizePhoneNumber(value?: string) {
    return (value || '').replace(/-/g, '');
  }

  private retrieveWirelessResponse(resp: any) {
    return resp.ResponseHeader?.ProductResponse?.find(
      (response: any) => response.Type.toLowerCase() === 'wireless'
    );
  }
}